import { useCallback, useState } from "react";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useRegistrationApiClient } from "@api/registration-api/use-registration-api-client";
import { FaroButton } from "@components/common/faro-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { useDataManagementContext } from "@context-providers/data-management/data-management-context";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useToast } from "@hooks/use-toast";
import { ButtonProps } from "@mui/material";
import { WorkflowState } from "@pages/data-management/data-management-types";
import { startRegistration } from "@pages/data-management/register-data-utils";
import { useAppDispatch } from "@store/store-helper";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props extends ButtonProps {
  /** Workflow state of the project */
  state: WorkflowState;
  /** Button caption. */
  caption: string;
  /** Flag if the button is only visible because of dev mode. */
  isDevMode: boolean;
  /** Optional callback when the button is clicked, called before the main action. */
  onClickBefore?: () => void;
}

/** Renders a button that triggers the data preparation workflow */
export function RegisterDataButton({
  state,
  caption,
  isDevMode,
  onClickBefore,
}: Props): JSX.Element {
  const { projectId } = useDataManagementContext();
  const { trackEvent } = useTrackEvent();
  const { showToast } = useToast();
  const { handleErrorWithToast } = useErrorContext();
  const dispatch = useAppDispatch();
  const projectApiClient = useProjectApiClient({ projectId });
  const progressApiClient = useProgressApiClient({ projectId });
  const registrationApiClient = useRegistrationApiClient({ projectId });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Triggers a multi cloud registration.
   * First checks if there are still scans being processed and shows a warning to the user.
   * After the multi cloud registration has successfully started the cloud registration tasks
   * are fetched in order to show the registration progress to the user.
   */
  const onClick = useCallback(async () => {
    setIsLoading(true);

    if (onClickBefore) {
      onClickBefore();
    }
    await startRegistration({
      progressApiClient,
      projectApiClient,
      registrationApiClient,
      trackEvent,
      showToast,
      dispatch,
      handleErrorWithToast,
    });

    setIsLoading(false);
  }, [
    handleErrorWithToast,
    showToast,
    trackEvent,
    dispatch,
    onClickBefore,
    progressApiClient,
    projectApiClient,
    registrationApiClient,
  ]);

  const isDisabled = state === "processError" && !isDevMode;

  return (
    <SphereTooltip
      title={isDisabled ?
        "You need to remedy the processing errors before registration can be started." :
        "Register the scans in the project."}
      enterDelay={isDisabled ? 0 : undefined}
    >
      <FaroButton
        onClick={onClick}
        isDisabled={isDisabled}
        isLoading={isLoading}
        dataTestId="sa-register-data-button"
      >
        {caption}
      </FaroButton>
    </SphereTooltip>
  );
}
